import { APIStatus } from "@/api/APIStatus.type";
import { Carousel } from "@/components/Carousel";
import { PlayerCardWithMetaRatingSkeleton } from "@/design-system/PlayerCard/PlayerCard";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { Fragment, ReactNode } from "react";
import { useMediaQuery } from "usehooks-ts";

export const GRID_ROWS = 2;
export const GRID_CARD_SCALE = 0.9;
export const CAROUSEL_CARD_SCALE = 0.8;

/**
 * A responsive grid layout. `className` holds the literal Tailwind classes
 * (Tailwind can't see dynamically built class strings), and `columns` mirrors
 * those breakpoints as data so the visible-card count stays in sync with the
 * actual rendered columns. Keep `columns` sorted by `minWidth` descending.
 */
export type GridLayout = {
  className: string;
  columns: { minWidth: number; cols: number }[];
};

export const PLAYER_GRID: GridLayout = {
  className: cn(
    "grid gap-2 justify-items-center",
    "min-[960px]:grid-cols-5",
    "min-[1120px]:grid-cols-6",
    "min-[1400px]:grid-cols-7",
  ),
  columns: [
    { minWidth: 1400, cols: 7 },
    { minWidth: 1120, cols: 6 },
    { minWidth: 960, cols: 5 },
  ],
};

/**
 * Mixed feed of players, evo players and SBCs. Same column track as
 * PLAYER_GRID, but SBC cards span 2 columns (col-span-2) so they read at the
 * same width as in the SBC-only grid. `grid-flow-dense` backfills the single
 * column gaps a span-2 card leaves behind, `items-start` top-aligns the
 * uneven card heights.
 */
export const ALL_GRID: GridLayout = {
  className: cn(
    "grid gap-2 justify-items-center grid-flow-dense items-start",
    "min-[960px]:grid-cols-5",
    "min-[1120px]:grid-cols-6",
    "min-[1400px]:grid-cols-7",
  ),
  columns: [
    { minWidth: 1400, cols: 7 },
    { minWidth: 1120, cols: 6 },
    { minWidth: 960, cols: 5 },
  ],
};

/**
 * Set cards carry a name, a progress line and a reward description, so they
 * stop at 3 columns however wide the page gets — a fourth column truncates all
 * three. With GRID_ROWS that is 6 cards.
 */
export const SBC_GRID: GridLayout = {
  className: cn(
    "grid gap-2 justify-items-center",
    "grid-cols-1",
    "min-[768px]:grid-cols-3",
  ),
  columns: [
    { minWidth: 768, cols: 3 },
    { minWidth: 0, cols: 1 },
  ],
};

/**
 * Current viewport width bucket, from a fixed set of breakpoints. Hooks must
 * run unconditionally, so we query the union of every breakpoint any layout
 * uses and collapse them to a single number. When adding a breakpoint to a
 * GridLayout, add it here too.
 */
function useViewportWidth() {
  const is1400 = useMediaQuery("(min-width: 1400px)");
  const is1120 = useMediaQuery("(min-width: 1120px)");
  const is960 = useMediaQuery("(min-width: 960px)");

  if (is1400) return 1400;
  if (is1120) return 1120;
  if (is960) return 960;
  return 0;
}

export function useGridColumns(layout: GridLayout) {
  const width = useViewportWidth();
  const match = layout.columns.find((c) => width >= c.minWidth);
  return match?.cols ?? layout.columns[layout.columns.length - 1].cols;
}

export function useVisibleCardCount(layout: GridLayout) {
  return useGridColumns(layout) * GRID_ROWS;
}

/**
 * Take items from the front until their combined cell span fills `maxCells`
 * (the visible 2-row capacity). Items wider than the remaining space are
 * skipped so a narrower later item can still top off the row — mirrors the
 * grid's `grid-flow-dense` packing, keeping the section at exactly 2 rows.
 */
function sliceToCells<T>(
  items: T[],
  maxCells: number,
  getSpan?: (item: T) => number,
): T[] {
  if (!getSpan) return items.slice(0, maxCells);
  const out: T[] = [];
  let used = 0;
  for (const item of items) {
    const span = Math.max(1, getSpan(item));
    if (used + span > maxCells) continue;
    out.push(item);
    used += span;
    if (used >= maxCells) break;
  }
  return out;
}

type Props<T> = {
  /** Tab chips / picker rendered above the content. */
  header: ReactNode;
  status: APIStatus;
  items: T[];
  getKey: (item: T, index: number) => string | number;
  renderGridCard: (item: T) => ReactNode;
  renderCarouselCard: (item: T) => ReactNode;
  /** Slice the grid to the visible 2-row count (trending). Default false. */
  limitToVisible?: boolean;
  /**
   * Grid cells an item occupies (e.g. a span-2 SBC card). Used with
   * `limitToVisible` so the slice fills exactly the visible rows by cell count
   * rather than item count. Default 1 for every item.
   */
  getItemSpan?: (item: T) => number;
  /** Grid layout: className + columns. Default PLAYER_GRID. */
  grid?: GridLayout;
  renderGridSkeleton?: (index: number) => ReactNode;
  renderCarouselSkeleton?: (index: number) => ReactNode;
  carouselSkeletonCount?: number;
  carousel?: {
    showArrows?: boolean;
    showScrollBar?: boolean;
    wrapperClassName?: string;
  };
};

const defaultGridSkeleton = () => (
  <PlayerCardWithMetaRatingSkeleton scale={GRID_CARD_SCALE} fullWidth />
);
const defaultCarouselSkeleton = () => (
  <PlayerCardWithMetaRatingSkeleton scale={CAROUSEL_CARD_SCALE} />
);

/**
 * Shared layout for the front-page sections: a tab picker followed by either a
 * horizontal carousel (touch devices) or a responsive 2-row grid (desktop),
 * with pending skeletons. Card + data are supplied via render props.
 */
export function FrontPageContent<T>({
  header,
  status,
  items,
  getKey,
  renderGridCard,
  renderCarouselCard,
  limitToVisible = false,
  getItemSpan,
  grid = PLAYER_GRID,
  renderGridSkeleton = defaultGridSkeleton,
  renderCarouselSkeleton = defaultCarouselSkeleton,
  carouselSkeletonCount = 8,
  carousel,
}: Props<T>) {
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const visibleCount = useVisibleCardCount(grid);
  const isLoading = status === "pending";

  if (isTouchDevice) {
    const carouselItems = isLoading
      ? Array.from({ length: carouselSkeletonCount }, (_, i) => (
          <Fragment key={i}>{renderCarouselSkeleton(i)}</Fragment>
        ))
      : items.map((item, i) => (
          <Fragment key={getKey(item, i)}>{renderCarouselCard(item)}</Fragment>
        ));

    const content = (
      <div className="-mx-4">
        <Carousel
          header={header}
          enableShadows
          showArrows={carousel?.showArrows}
          showScrollBar={carousel?.showScrollBar}
          items={carouselItems}
        />
      </div>
    );

    return carousel?.wrapperClassName ? (
      <div className={carousel.wrapperClassName}>{content}</div>
    ) : (
      content
    );
  }

  const visibleItems = limitToVisible
    ? sliceToCells(items, visibleCount, getItemSpan)
    : items;

  const gridItems = isLoading
    ? Array.from({ length: visibleCount }, (_, i) => (
        <Fragment key={i}>{renderGridSkeleton(i)}</Fragment>
      ))
    : visibleItems.map((item, i) => (
        <Fragment key={getKey(item, i)}>{renderGridCard(item)}</Fragment>
      ));

  return (
    <div className="flex flex-col gap-y-3">
      {header}
      <div className={grid.className}>{gridItems}</div>
    </div>
  );
}
